import type { FC } from 'react';
import React from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import Image from '@atlaskit/image';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Stack, xcss } from '@atlaskit/primitives';

import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay';
import { AUTOMATION_MANUAL_TRIGGERS, ExperienceFailure } from '@confluence/experience-tracker';

import EmptyListImage from '../assets/empty-automation-list.png';
import { useAutomationMenu } from '../common/ConfluenceManualRulesContainer';
import { useAutomationPermissionService } from '../permission-service';

const i18n = defineMessages({
	emptyAutomationModalAdminDescription: {
		id: 'automation-menu.modal.empty.automations.admin.description',
		defaultMessage:
			'Use the manual trigger to populate a menu of selections that anyone, or people you specify, can apply as one-click automations to a page.',
		description:
			'A description shown in the automation menu when the rule list is empty for an admin.',
	},
	emptyAutomationsUserDescription: {
		id: 'automation-menu.modal.empty.automations.user.description',
		defaultMessage:
			'Admins haven’t configured any page automations for this space yet. When they do, it might include one-click actions like changing page restrictions.',
		description:
			'A description shown in the automation menu when the rule list is empty for a user.',
	},
	learnMoreLink: {
		id: 'automation-menu.empty-state.learn-more.link',
		defaultMessage: 'Learn more about the manual trigger',
		description:
			'The text of a link that takes a user to the documentation for manually triggered rules in Automation for Confluence.',
	},
	emptyAutomationListImageAlt: {
		id: 'automation-menu.empty-state.image.alt',
		defaultMessage: 'Empty automation list image',
		description: 'Alternative text for the image that renders when the automation list is empty.',
	},
});

const emptyStateStyles = xcss({
	paddingLeft: 'space.300',
	paddingRight: 'space.300',
	marginTop: 'space.500',
});

const imageStyles = xcss({
	marginTop: 'space.050',
	marginBottom: 'space.300',
});

const descriptionStyles = xcss({
	width: '360px',
	textAlign: 'center',
});

export const AutomationModalEmptyState: FC = () => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { spaceKey, analyticsSource } = useAutomationMenu();
	const { canManageAutomations, error: permissionServiceError } =
		useAutomationPermissionService(spaceKey);

	const descriptionContent = canManageAutomations
		? i18n.emptyAutomationModalAdminDescription
		: i18n.emptyAutomationsUserDescription;

	return (
		<Stack xcss={emptyStateStyles} alignInline="center" alignBlock="center" grow="fill">
			<Box xcss={imageStyles}>
				<Image alt={formatMessage(i18n.emptyAutomationListImageAlt)} src={EmptyListImage} />
			</Box>
			{permissionServiceError && (
				<ErrorDisplay error={permissionServiceError}>
					<ExperienceFailure
						name={AUTOMATION_MANUAL_TRIGGERS}
						error={permissionServiceError}
						attributes={{ location: 'emptyState', source: analyticsSource }}
					/>
				</ErrorDisplay>
			)}
			<Box xcss={descriptionStyles}>
				<FormattedMessage {...descriptionContent} />
				{canManageAutomations && (
					<div>
						<Button
							appearance="link"
							spacing="none"
							href="https://www.atlassian.com/platform/automation"
							target="_blank"
							onClick={() => {
								createAnalyticsEvent({
									type: 'sendUIEvent',
									data: {
										action: 'clicked',
										actionSubject: 'link',
										actionSubjectId: 'learnMore',
										source: analyticsSource,
									},
								}).fire();
							}}
						>
							<FormattedMessage {...i18n.learnMoreLink} />
						</Button>
					</div>
				)}
			</Box>
		</Stack>
	);
};
